import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import FlashContext from "components/FlashMessages/FlashContext";
import usePrevious from "hooks/usePrevious";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage, TemplateDeploymentType } from "types/generated";
import { PollingIntervalGroups } from "apollo/constants";

import {
  DEPLOYMENTS_LIMIT,
  DEPLOYMENTS_LIMIT_STORAGE_KEY,
  FILTER_TYPE,
  initialSortDirection,
  initialSortOption,
} from "../constants";
import { SEARCH_TEMPLATE_DEPLOYMENTS } from "../gql";

type UseDeploymentsArgs = {
  id?: string;
  versionId?: string;
  excludeTestDeployments?: boolean;
};

const useDeployments = ({ id, versionId, excludeTestDeployments }: UseDeploymentsArgs) => {
  const [rowsPerPage] = useLocalStorage<number>(DEPLOYMENTS_LIMIT_STORAGE_KEY);
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const resetPaginationVariables = useMemo(() => {
    const predicates = filterVariables.predicates ? [...filterVariables.predicates] : [];

    // Add blueprintSlug predicate if provided
    if (id) {
      predicates.push({
        field: "blueprintSlug",
        exclude: null,
        constraint: {
          booleanEquals: null,
          enumEquals: null,
          stringEquals: [id],
          stringMatches: null,
          hierarchyNodeValueEquals: null,
          hierarchyNode: null,
          timeInRange: null,
          timeInLast: null,
        },
      });
    }

    if (versionId) {
      predicates.push({
        field: "blueprintVersionSlug",
        exclude: null,
        constraint: {
          booleanEquals: null,
          enumEquals: null,
          stringEquals: [versionId],
          stringMatches: null,
          hierarchyNodeValueEquals: null,
          hierarchyNode: null,
          timeInRange: null,
          timeInLast: null,
        },
      });
    }

    if (excludeTestDeployments) {
      predicates.push({
        field: "deploymentType",
        exclude: true,
        constraint: {
          booleanEquals: null,
          enumEquals: [TemplateDeploymentType.Test],
          stringEquals: null,
          stringMatches: null,
          hierarchyNodeValueEquals: null,
          hierarchyNode: null,
          timeInRange: null,
          timeInLast: null,
        },
      });
    }

    return {
      fullTextSearch: filterVariables.searchInput,
      predicates,
      ...(filterVariables.sortOptionFields && { orderBy: filterVariables.sortOptionFields }),
    };
  }, [filterVariables, id, versionId, excludeTestDeployments]);

  const previousResetPaginationVariables = usePrevious(resetPaginationVariables);

  // Reset when variables changed
  useEffect(() => {
    if (!isEqual(previousResetPaginationVariables, resetPaginationVariables)) {
      setRequestedPage(null);
      setAfter(null);
    }
  }, [previousResetPaginationVariables, resetPaginationVariables]);

  const initialVariables = useMemo(() => {
    return {
      input: {
        ...resetPaginationVariables,
        first: rowsPerPage || DEPLOYMENTS_LIMIT,
        after,
        requestedPage,
      },
    };
  }, [resetPaginationVariables, rowsPerPage, after, requestedPage]);

  const skipQuery = !filterVariablesLoaded;

  const { error, loading, data, networkStatus, previousData, refetch } = usePolledQuery(
    SEARCH_TEMPLATE_DEPLOYMENTS,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      // avoid request executing twice while fetchMore
      nextFetchPolicy: "cache-first",
      // APOLLO CLIENT UPDATE
      skip: skipQuery,
    }
  );

  const loadDeployments = useCallback(
    async ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        setAfter(cursor);
        setRequestedPage(requestedPage);
      } catch (error) {
        onError(error);
      }
    },
    [onError]
  );

  const initialLoading =
    (loading && networkStatus === NetworkStatus.loading && !data?.searchTemplateDeployments) ||
    !filterVariablesLoaded;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const deployments = useMemo(
    () =>
      data?.searchTemplateDeployments?.edges.map((edge) => edge.node) ||
      previousData?.searchTemplateDeployments?.edges.map((edge) => edge.node) ||
      [],
    [data?.searchTemplateDeployments?.edges, previousData?.searchTemplateDeployments?.edges]
  );

  return {
    refetch,
    loadDeployments,
    error,
    deployments,
    initialLoading,
    noResults: !loading && !data?.searchTemplateDeployments,
    endCursor: data?.searchTemplateDeployments?.pageInfo.endCursor,
    startCursor: data?.searchTemplateDeployments?.pageInfo.startCursor,
    isEmpty: data && !deployments.length,
    loadingContent,
    predicates: filterVariables.predicates,
    paginationResetKey: resetPaginationVariables,
  };
};

export default useDeployments;
